import {
  VStack,
  Box,
  Stack,
  Flex,
  Badge,
  Card,
  Separator,
  Text,
  Link,
  StackProps
} from '@chakra-ui/react';

interface CandidateOverviewSectionProps extends StackProps {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  linkedin?: string;
  fitPositions: string[];
  resume: Resume;
}

export function CandidateOverviewSection({
  firstName,
  lastName,
  email,
  phone,
  linkedin,
  fitPositions,
  resume,
  ...props
}: CandidateOverviewSectionProps) {
  return (
    <VStack w="100%" align="flex-start" gap={{ base: '16px', md: '24px' }} {...props}>
      <Box>
        <Text
          fontSize={{ base: '16px', md: '18px' }}
          fontWeight="semibold"
          color="neutrals.800"
          mb={{ base: '8px', md: '12px' }}
        >
          Personal Information
        </Text>
        <Stack gap={{ base: '8px', md: '12px' }}>
          <Stack direction={{ base: 'column', sm: 'row' }} gap={{ base: '4px', sm: '0' }}>
            <Text
              fontSize={{ base: '14px', md: '16px' }}
              color="neutrals.600"
              w={{ base: 'auto', sm: '120px' }}
              fontWeight="medium"
            >
              First Name:
            </Text>
            <Text fontSize={{ base: '14px', md: '16px' }} color="neutrals.800" fontWeight="medium">
              {firstName}
            </Text>
          </Stack>
          <Stack direction={{ base: 'column', sm: 'row' }} gap={{ base: '4px', sm: '0' }}>
            <Text
              fontSize={{ base: '14px', md: '16px' }}
              color="neutrals.600"
              w={{ base: 'auto', sm: '120px' }}
              fontWeight="medium"
            >
              Last Name:
            </Text>
            <Text fontSize={{ base: '14px', md: '16px' }} color="neutrals.800" fontWeight="medium">
              {lastName}
            </Text>
          </Stack>
          <Stack direction={{ base: 'column', sm: 'row' }} gap={{ base: '4px', sm: '0' }}>
            <Text
              fontSize={{ base: '14px', md: '16px' }}
              color="neutrals.600"
              w={{ base: 'auto', sm: '120px' }}
              fontWeight="medium"
            >
              Email:
            </Text>
            <Text
              fontSize={{ base: '14px', md: '16px' }}
              color="neutrals.800"
              fontWeight="medium"
              wordBreak="break-all"
            >
              {email}
            </Text>
          </Stack>
          <Stack direction={{ base: 'column', sm: 'row' }} gap={{ base: '4px', sm: '0' }}>
            <Text
              fontSize={{ base: '14px', md: '16px' }}
              color="neutrals.600"
              w={{ base: 'auto', sm: '120px' }}
              fontWeight="medium"
            >
              Phone:
            </Text>
            <Text fontSize={{ base: '14px', md: '16px' }} color="neutrals.800" fontWeight="medium">
              {phone || 'Not provided'}
            </Text>
          </Stack>
          {linkedin && (
            <Stack direction={{ base: 'column', sm: 'row' }} gap={{ base: '4px', sm: '0' }}>
              <Text
                fontSize={{ base: '12px', md: '14px' }}
                color="neutrals.600"
                w={{ base: 'auto', sm: '120px' }}
                fontWeight="medium"
              >
                LinkedIn:
              </Text>
              <Link
                href={linkedin.startsWith('http') ? linkedin : `https://${linkedin}`}
                target="_blank"
                rel="noopener noreferrer"
                _hover={{ textDecoration: 'underline' }}
              >
                <Text fontSize={{ base: '12px', md: '14px' }} color="blue.600" fontWeight="medium">
                  View Profile
                </Text>
              </Link>
            </Stack>
          )}
        </Stack>
      </Box>

      {fitPositions.length > 0 && (
        <Box>
          <Text
            fontSize={{ base: '16px', md: '18px' }}
            fontWeight="semibold"
            color="neutrals.800"
            mb={{ base: '8px', md: '12px' }}
          >
            Categories
          </Text>
          <Flex gap={{ base: '8px', md: '12px' }} wrap="wrap">
            {fitPositions.map((tag) => (
              <Badge
                key={tag}
                borderRadius="full"
                p={{ base: '3px 12px', md: '4px 16px' }}
                bg="blue.100"
              >
                <Text
                  fontSize={{ base: '12px', md: '14px' }}
                  color="neutrals.900"
                  fontWeight="medium"
                >
                  {tag}
                </Text>
              </Badge>
            ))}
          </Flex>
        </Box>
      )}

      <Box w={{ base: '100%', md: '80%' }}>
        <Text
          fontSize={{ base: '16px', md: '18px' }}
          fontWeight="semibold"
          color="neutrals.800"
          mb={{ base: '8px', md: '12px' }}
        >
          Work Experience
        </Text>
        {resume.work_experiences?.length > 0 ? (
          <VStack align="flex-start" gap={{ base: '12px', md: '20px' }} w="100%">
            {resume.work_experiences.slice(0, 5).map((exp: WorkExperience, index: number) => (
              <Card.Root
                key={index}
                bg="neutrals.50"
                borderRadius="12px"
                p={{ base: '12px', md: '20px' }}
                w="100%"
              >
                <VStack align="flex-start" gap={{ base: '6px', md: '8px' }}>
                  <Flex
                    justifyContent="space-between"
                    w="100%"
                    direction={{ base: 'column', sm: 'row' }}
                    gap={{ base: '4px', sm: '0' }}
                  >
                    <Text
                      fontSize={{ base: '14px', md: '16px' }}
                      fontWeight="semibold"
                      color="neutrals.900"
                    >
                      {exp.job_role}
                    </Text>
                    <Text
                      fontSize={{ base: '12px', md: '14px' }}
                      color="neutrals.700"
                      fontWeight="medium"
                    >
                      {exp.company_name}
                    </Text>
                  </Flex>
                  <Text
                    fontSize={{ base: '12px', md: '14px' }}
                    color="neutrals.700"
                    lineHeight="1.4"
                  >
                    {exp.time}
                    {exp.duration && (
                      <Text
                        ml={{ base: '8px', md: '12px' }}
                        as="span"
                        fontSize={{ base: '12px', md: '14px' }}
                        color="neutrals.700"
                      >
                        • {exp.duration}
                      </Text>
                    )}
                    {exp.location && (
                      <>
                        <Text
                          ml={{ base: '8px', md: '12px' }}
                          as="span"
                          fontSize={{ base: '12px', md: '14px' }}
                          color="neutrals.700"
                        >
                          |
                        </Text>
                        <Text
                          ml={{ base: '8px', md: '12px' }}
                          as="span"
                          fontSize={{ base: '12px', md: '14px' }}
                          color="neutrals.700"
                        >
                          {exp.location}
                        </Text>
                      </>
                    )}
                  </Text>
                </VStack>
              </Card.Root>
            ))}
          </VStack>
        ) : (
          <Text fontSize={{ base: '12px', md: '14px' }} color="neutrals.600">
            No work experience information available.
          </Text>
        )}
      </Box>

      {resume.skills.length > 0 && (
        <Box w={{ base: '100%', md: '90%' }}>
          <Text
            fontSize={{ base: '16px', md: '18px' }}
            fontWeight="semibold"
            color="neutrals.800"
            mb={{ base: '16px', md: '32px' }}
          >
            Skills
          </Text>
          {resume.skills?.length > 0 && (
            <VStack align="flex-start" gap={{ base: '20px', md: '32px' }} w="100%">
              {resume.skills.map((skill: Skill, index: number) => (
                <Box key={index} p={{ base: '0 8px', md: '0 20px' }} w="100%">
                  <Flex
                    gap={{ base: '6px', md: '8px' }}
                    alignItems={{ base: 'flex-start', lg: 'center' }}
                    direction={{ base: 'column', lg: 'row' }}
                  >
                    {resume.skills.length > 1 && skill.section !== 'Skills' && (
                      <Box
                        minW={{ base: 'auto', lg: '200px' }}
                        maxW={{ base: '100%', lg: '200px' }}
                        mb={{ base: '8px', lg: '0' }}
                      >
                        <Text
                          fontSize={{ base: '14px', md: '16px' }}
                          fontWeight="semibold"
                          color="neutrals.700"
                        >
                          {skill.section}
                        </Text>
                      </Box>
                    )}

                    <Flex align="flex-start" gap={{ base: '8px', md: '12px' }} wrap="wrap" flex="1">
                      {skill.contents.map((content, idx) => (
                        <Badge
                          key={idx}
                          bg="primary.300"
                          fontSize={{ base: '12px', md: '14px' }}
                          color="neutrals.700"
                          fontWeight="medium"
                          whiteSpace="nowrap"
                          overflow="hidden"
                          textOverflow="ellipsis"
                          maxW="100%"
                          p={{ base: '3px 6px', md: '4px 8px' }}
                          borderRadius="12px"
                        >
                          {content}
                        </Badge>
                      ))}
                    </Flex>
                  </Flex>
                  <Separator mt={{ base: '16px', md: '32px' }} />
                </Box>
              ))}
            </VStack>
          )}
        </Box>
      )}
    </VStack>
  );
}
